import DialogTitle from "@mui/material/DialogTitle";
import React, {Fragment, useEffect, useState} from "react";
import {Button, CircularProgress, DialogContent, Grid, Paper, ToggleButtonGroup} from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import {useTranslation} from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import {useSelector} from "react-redux";
import {currentMap, statuses, wardPatients} from "../../store/reducers/ward";
import LocalHotelIcon from '@mui/icons-material/LocalHotel';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import FunctionsIcon from '@mui/icons-material/Functions';
import LockIcon from '@mui/icons-material/Lock';
import SingleBedIcon from '@mui/icons-material/SingleBed';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PanToolIcon from '@mui/icons-material/PanTool';
import LogoutIcon from '@mui/icons-material/Logout';
import _ from "lodash";
import clsx from "clsx";
import moment from "moment";
import config from "../../app/config";
import API from "../../app/api";
import {Doughnut} from "react-chartjs-2";
import {useTheme} from "@mui/material/styles";
import ToggleButton from "@mui/material/ToggleButton";
import {AgyleNamedIcon} from "../../components/icons/agyle";
import {calculateArrivalDate} from '../../util/patientUtil';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: theme.palette.background.default,
    },
    dialogTitle: {
        textAlign: "center",
        padding: "10px",
        fontWeight: "bold",
        fontSize: 24
    },
    dialogContent: {
        height: "100%",
    },
    button: {
        background: "#fff",
        color: "#000",
        width: "10%",
        "&:hover": {
            background: "#fff",
        }
    },
    box: {
        minHeight: 110,
        background: "#3c78d8",
        borderRadius: 10,
        padding: 6,
        boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#a4c2f4" : "#172840"}`
    },
    boxText: {
        textAlign: "right",
        fontSize: 14,
        color: "#fff"
    },
    boxCounter: {
        fontSize: 36,
        color: "#fff"
    },
    icon: {
        color: "#fff",
        fontSize: 50,
    },
    paper: {
        margin: "10px",
        borderRadius: "7px",
        background: theme.palette.background.paper,
        padding: "0px 10px 10px 10px",
    },
    wardBox: {
        background: theme.palette.background.default,
        borderRadius: 10,
    },
    wardBoxTitle: {
        height: 40,
        borderRadius: "10px 10px 0 0",
        padding: 5,
        background: "#3c78d8",
        color: "#fff",
        textAlign: "center",
        fontWeight: "bold",
        fontSize: "large"
    },
    wardBoxInfo: {
        padding: "5px 0",
        textAlign: "center",
        boxShadow: `2px 2px 2px ${theme.palette.background.paper}`
    },
    wardBoxIcon: {
        marginRight: 5,
        fontSize: "xx-large",
        "&.total": {
            color: theme.palette.mode === 'light' ? "#434343" : "#d9d9d9"
        },
        "&.lock": {
            color: theme.palette.mode === 'light' ? "#DCAA12" : "#ffdd77"
        },
        "&.occupied": {
            color: theme.palette.mode === 'light' ? "#a61c00" : "#d94a2d"
        },
        "&.free": {
            color: theme.palette.mode === 'light' ? "#6aa84f" : "#abef8d"
        },
    },
    wardBoxCounter: {
        fontSize: 26,
        marginLeft: 5,
        verticalAlign: "super"
    },
    longestTimeBox: {
        minHeight: 150,
        padding: 5,
        borderRadius: 10,
        color: "#fff",
        "&.seen": {
            background: theme.palette.mode === 'light' ? "#ff9900" : "#ffbe5c",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#f9cb9c" : "#ff9900"}`
        },
        "&.longest": {
            background: theme.palette.mode === 'light' ? "#a61c00" : "#b34630",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#cc4125" : "#b34630"}`
        },
        "&.average": {
            background: theme.palette.mode === 'light' ? "#4a86e8" : "#709de5",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#a4c2f4" : "#4773bf"}`
        },
    },
    longestTimeIcon: {
        color: "#fff",
        fontSize: 50
    },
    longestTimeDescription: {
        fontSize: "small",
        textAlign: "right",
        fontStyle: "italic"
    },
    longestTimeMainText: {
        fontSize: "large",
        marginLeft: 10
    },
    longestTimeFormat: {
        textAlign: "right",
        fontSize: 36,
    },
    longestTimeFormatLegend: {
        textAlign: "right",
        fontSize: "small",
        opacity: 0.7
    },
    breachBox: {
        padding: 5,
        color: "#fff",
        borderRadius: 10,
        "&.one": {
            background: theme.palette.mode === 'light' ? "#01AF69" : "#44bb8b",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#07B670" : "#09462e"}`
        },
        "&.two": {
            background: theme.palette.mode === 'light' ? "#ffb74d" : "#ffbe5c",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#FFC46E" : "#ff9900"}`
        },
        "&.three": {
            background: theme.palette.mode === 'light' ? "#e69138" : "#ef902c",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#E79B4C" : "#b96206"}`
        },
        "&.four": {
            background: theme.palette.mode === 'light' ? "#DC4A5D" : "#f36577",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#E15B6C" : "#ff3952"}`
        },
        "&.over": {
            background: theme.palette.mode === 'light' ? "#A32626" : "#ad3c3c",
            boxShadow: `2px 2px 2px ${theme.palette.mode === 'light' ? "#A33636" : "#851010"}`
        },
    },
    breachText: {
        textAlign: "center",
        fontSize: "small"
    },
    breachCounter: {
        marginRight: 10,
        fontSize: 40
    },
    chart: {
        float: "right"
    },
    toggleButtons: {
        textAlign: "center",
        width: "100%"
    },
    toggleButtonsGroup: {
        width: "100%",
        padding: "0 10px"
    },
    toggle: {
        display: "block",
        padding: "14px 28px",
        textAlign: "center",
        width: "100%"
    },
    loading: {
        position: "absolute",
        marginLeft: "auto",
        marginRight: "auto",
        paddingTop: "30%",
        left: 0,
        right: 0,
        textAlign: "center",
        pointerEvents: "none"
    }
}));

// TODO Delete this after new map
const WLIST_NAME_MAPPER = {
    "AMBULANCEQueue": "AMBULANCE QUEUE",
    "LANCEQueue": "AMBULANCE QUEUE",
    "AMBULANCEList": "AMBULANCE LIST",
    "LANCEList": "AMBULANCE LIST",
    "AWAITINGTriage": "AWAITING TRIAGE",
    "Child WaitPre Triage": "CHILD WAIT PRE TRIAGE",
    "Post TriageUTC": "POST TRIAGE UTC",
    "PostTriage": "POST TRIAGE",
    "UTC WAITINGPre Triage": "UTC WAITING PRE TRIAGE",
    "TC WAITINGPre Triage": "UTC WAITING PRE TRIAGE",
    "AWAITINGTriage (POD)": "AWAITING TRIAGE",
    "DISCHARGE(notes to be completed)": "DISCHARGE",
    "rge": "DISCHARGE",
    "MINORSLedger": "MINORS LEDGER",
    "OUT ofDepartment": "OUT OF DEPARTMENT",
    "POSTTriage (POD)": "POST TRIAGE",
    "WAITINGRoom": "WAITING ROOM",
    "Orthopaedic Outpatients": "ORTHOPAEDIC OUTPATIENTS",
    "ACAWaiting": "ACA WAITING",
    "AMBULANCECohort List": "AMBULANCE COHORT LIST",
    "LANCECohort List": "AMBULANCE COHORT LIST",
    "AWAITINGTriage UTC": "AWAITING TRIAGE UTC",
    "Blue - Wait(MAX 5)": "BLUE WAIT",
    "ChildrenWaiting": "Children Waiting",
    "Wait(MAX 5)": "BLUE WAIT",
    "CHILD WAITPre Triage": "CHILD WAIT PRE TRIAGE",
    "CORRIDORList": "CORRIDOR LIST",
    "List": "CORRIDOR LIST",
    "DISCHARGE(Max 10)": "DISCHARGE",
    "HARGE(Max 10)": "DISCHARGE",
    "POST TRIAGEUTC": "POST TRIAGE UTC",
    "WaitTriage": "WAIT TRIAGE",
    "aitTriage": "WAIT TRIAGE",
    "T TRIAGEUTC": "POST TRIAGE UTC",
    "MINORS A": "MAJORS A",
}

const Dashboard = (props) => {
    const {t, i18n} = useTranslation();
    const classesLocal = useStyles();
    const theme = useTheme();
    const controller = new AbortController();

    // Patients and locked
    const wardPatientsObject = useSelector(wardPatients);
    const statusesObject = useSelector(statuses);
    const newCurrentMap = useSelector(currentMap);

    // Dashboard Mode
    const newCSMForm = config?.modules?.forms?.bedBooking?.enableBedRequestForm;
    const [mode, setMode] = useState("today");

    // Arrival Mode
    const arrivalMode = props.arrivalMode;

    // Loading
    const [loading, setLoading] = useState(false);

    // Dashboard Stats
    // const [dashboardStats, setDashboardStats] = useState(initState);
    const [dashboardStats, setDashboardStats] = useState({});
    const [intervalCheckState, setIntervalCheckState] = useState({
        preReg: 0,
        counter: {
            one: 0,
            two: 0,
            three: 0,
            four: 0,
            over4: 0,
            next12: 0,
            over12: 0,
        },
        ambulanceAvarage: 0,
        breaches4h: 0,
        ambulance15min: 0,
        ambulanceAll: 0,
        discharges: 0,
        dischargesFiltered: 0,
        ambulanceOver30min: 0,
        ambulanceOver60min: 0,
        breaches12h: 0
    });

    // CleanUp
    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    // Get dashboard stats
    useEffect(() => {
        let currentPt = 0;
        let currentAttendance = 0;
        let beds = [];
        let edau = 0;
        let refAll = 0;
        let refOpen = 0;
        let waitingWard = 0;
        let longestSeen;
        let longestAttendance;
        let averageSeen = {
            counter: 0,
            min: 0
        };
        let longestClerking;
        let averageClerking = {
            counter: 0,
            min: 0
        };
        let averageClerkingSeen = {
            counter: 0,
            min: 0
        };
        let averageHandover = {
            counter: 0,
            min: 0
        };
        let clinicalConclusion = 0;
        let bedReq = 0;
        let bedReady = 0;
        let ambulanceArrivalAwaiting = 0;
        let ambulanceArrivalBreaches = 0;
        let ambulanceArrivalBreaches15 = 0;
        let ambulanceArrivalBreaches30 = 0;
        let ambulanceArrivalBreaches60 = 0;

        // Temp data
        let longSeenData;
        let longestAttendanceDate;
        let longClerkingData;

        const startData = mode === "today"
            ? moment().utc().startOf('day').toISOString()
            : moment().subtract(1, 'days').toISOString();

        _.forEach(wardPatientsObject, element => {
            let elementHidden = false;
            if (_.startsWith(element?.bed, "wlist") || !element?.bed) {
                if (element.admission?.locationDescription === "DISCHARGE") {
                    elementHidden = true;
                }
            }

            if (!elementHidden) {
                // Update Beds
                beds.push(element.bed);

                if (element?.wardId !== "0EDAU" && moment(element?.admission?.startDate).isSameOrAfter(moment(startData))) {
                    currentAttendance++;
                }

                // For Patients not in EDAU
                if (element?.wardId !== "0EDAU" && element?.admission?.status !== "DISCHARGE_REQUESTED") {
                    // Waiting list check
                    if (_.startsWith(element?.bed, "wlist") || !element?.bed) {
                        waitingWard++;
                    }


                    // Referrals
                    if (!_.isEmpty(element?.studies?.referrals?.ReferralsToOtherServices)) {
                        let haveOpen = false;
                        refAll++;

                        for (let i = 0; i < element.studies.referrals.ReferralsToOtherServices.length; i++) {
                            haveOpen = element.studies.referrals.ReferralsToOtherServices[i].ReferredToServiceAssessmentTime === "" ||
                                element.studies.referrals.ReferralsToOtherServices[i].ReferredToServiceAssessmentDate === "";
                            if (haveOpen) break;
                        }
                        if (haveOpen) {
                            refOpen++;
                        }
                    }

                    currentPt++;

                    // Longest Attendance
                    if (!longestAttendance) {
                        longestAttendance = element.__id;
                        longestAttendanceDate = element?.admission?.startDate;
                    } else {
                        if (moment(element?.admission?.startDate).isBefore(moment(longestAttendanceDate))) {
                            longestAttendance = element.__id;
                            longestAttendanceDate = element?.admission?.startDate;
                        }
                    }

                    // Longest Wait for Triage
                    if (_.isNil(element.triage?.discriminator)) {
                        if (!longestSeen) {
                            longestSeen = element.__id;
                            longSeenData = moment().diff(moment(element?.admission?.startDate), "minutes");
                        } else {
                            const time = moment().diff(moment(element?.admission?.startDate), "minutes");
                            if (time > longSeenData) {
                                longestSeen = element.__id;
                                longSeenData = time;
                            }
                        }
                    }

                    // Longest Wait for Clerking Clinician
                    if (_.isNil(element?.firstClinician?.who)) {
                        if (!longestClerking) {
                            longestClerking = element.__id;
                            longClerkingData = moment().diff(moment(element?.admission?.startDate), "minutes");
                        } else {
                            const time = moment().diff(moment(element?.admission?.startDate), "minutes");
                            if (time > longClerkingData) {
                                longestClerking = element.__id;
                                longClerkingData = time;
                            }
                        }
                    }

                    // Current Average Ambulance Handover Time
                    if (element.ambulanceHandover?.when) {
                        let time = moment(element.ambulanceHandover?.when).diff(moment(element?.admission?.startDate), "minutes");

                        if (time > 0) {
                            averageHandover = {
                                counter: averageHandover.counter + 1,
                                min: averageHandover.min + moment(element.ambulanceHandover?.when).diff(moment(element?.admission?.startDate), "minutes"),
                            };
                        }
                    }

                    // Current Average Triage and Triage to Clerking Clinician
                    if (element.triage?.discriminator) {
                        const countMin = moment(element.triage?.when).diff(moment(element?.admission?.startDate), "minutes");
                        if (!_.isNaN(countMin)) {
                            averageSeen = {
                                counter: averageSeen.counter + 1,
                                min: averageSeen.min + countMin
                            };
                        }
                        if (element.firstClinician?.when) {
                            const countMin = moment(element.firstClinician?.when).diff(moment(element.triage?.when), "minutes");
                            if (!_.isNaN(countMin) && countMin > 0) {
                                averageClerkingSeen = {
                                    counter: averageClerkingSeen.counter + 1,
                                    min: averageClerkingSeen.min + countMin
                                };
                            }
                        }
                    }

                    // Current Average Clerking Clinician
                    if (element.firstClinician?.when) {
                        const countMin = moment(element.firstClinician?.when).diff(moment(element?.admission?.startDate), "minutes");
                        if (!_.isNaN(countMin)) {
                            averageClerking = {
                                counter: averageClerking.counter + 1,
                                min: averageClerking.min + countMin
                            };
                        }
                    }

                    // Count Arrival Mode
                    if (arrivalMode.includes(element?.admission?.arrivalModeCode)) {
                        if (!element?.ambulanceHandover?.when) {
                            ambulanceArrivalAwaiting++;

                            const duration = moment().diff(moment(element.admission?.startDate), "minute");

                            if (!_.isNaN(duration) && duration >= 15) {
                                ambulanceArrivalBreaches++;

                                if (duration <= 30) ambulanceArrivalBreaches15++;
                                else if (duration < 60) ambulanceArrivalBreaches30++;
                                else ambulanceArrivalBreaches60++;
                            }
                        }
                    }

                    // Clinical Conclusion Counter
                    if (element?.clinicalConclusion?.status) {
                        if (element?.clinicalConclusion?.status !== "Deceased") {
                            clinicalConclusion++;
                        }
                    }

                    // Bed Ready and Requested Counter
                    if (!newCSMForm && element?.csmContactedTime?.bed) {
                        if (element?.csmContactedTime?.bed === "req") bedReq++;
                        if (element?.csmContactedTime?.bed === "ready") bedReady++;
                    }

                    if (newCSMForm && element?.studies?.bedRequest && !element?.studies?.bedRequest?.canceled) {
                        if (element?.studies?.bedRequest?.allocated || element?.studies?.bedRequest?.ready) bedReady++;
                        else if (element?.studies?.bedRequest?.requested) bedReq++;
                    }
                } else {
                    // Check EDAU
                    if (element?.admission?.status !== "DISCHARGE_REQUESTED") {
                        edau++;
                    }
                }

            }
        });

        setDashboardStats(prevState => ({
            ...prevState,
            ...intervalCheckState,
            beds,
            currentPt,
            currentAttendance,
            ptEDAU: edau,
            refAll,
            refOpen,
            waitingWard,
            seen: longestSeen,
            seenTime: longSeenData,
            clerking: longestClerking,
            clerkingTime: longClerkingData,
            longest: longestAttendance,
            average: averageSeen.counter > 0 ? Math.floor(averageSeen.min / averageSeen.counter) : 0,
            clerkingAverage: averageClerking.counter > 0 ? Math.floor(averageClerking.min / averageClerking.counter) : 0,
            ambulanceHandover: averageHandover.counter > 0 ? Math.floor(averageHandover.min / averageHandover.counter) : 0,
            clerkingSeen: averageClerkingSeen.counter > 0 ? Math.floor(averageClerkingSeen.min / averageClerkingSeen.counter) : 0,
            clinicalConclusion,
            bedReq,
            bedReady,
            ambulanceArrivalAwaiting,
            ambulanceArrivalBreaches,
            ambulanceArrivalBreaches15,
            ambulanceArrivalBreaches30,
            ambulanceArrivalBreaches60,
            dataChart: {
                labels: ['0-1hrs', '1-2hrs', '2-3hrs', '3-4hrs', 'Breaches (4th hour)', 'Breaches (12th hour)'],
                datasets: [
                    {
                        label: '# of ED Breach Profile',
                        data: [
                            intervalCheckState?.counter?.one,
                            intervalCheckState?.counter?.two,
                            intervalCheckState?.counter?.three,
                            intervalCheckState?.counter?.four,
                            intervalCheckState?.counter?.over4,
                            intervalCheckState?.counter?.over12
                        ],
                        backgroundColor: [
                            `${theme.palette.mode === 'light' ? "#42EEA9" : "#01AF69"}`,
                            `${theme.palette.mode === 'light' ? "#FFCD83" : "#ffbe5c"}`,
                            `${theme.palette.mode === 'light' ? "#E6A969" : "#ef902c"}`,
                            `${theme.palette.mode === 'light' ? "#DD6877" : "#f36577"}`,
                            `${theme.palette.mode === 'light' ? "#A43838" : "#ad3c3c"}`,
                            `${theme.palette.mode === 'light' ? "#862C2C" : "#872222"}`,
                        ],
                        borderColor: [
                            `${theme.palette.mode === 'light' ? "#00E489" : "#09462e"}`,
                            `${theme.palette.mode === 'light' ? "#FAA72D" : "#ff9900"}`,
                            `${theme.palette.mode === 'light' ? "#E68117" : "#b96206"}`,
                            `${theme.palette.mode === 'light' ? "#E0253E" : "#ff3952"}`,
                            `${theme.palette.mode === 'light' ? "#A61818" : "#851010"}`,
                            `${theme.palette.mode === 'light' ? "#8A1313" : "#640c0c"}`,
                        ],
                        borderWidth: 1,
                    },
                ],
            }
        }));
    }, [wardPatientsObject, intervalCheckState, mode]);

    // Interval check stats for dashboard
    useEffect(() => {
        const timeout = config?.dashboard?.timeout;

        let cancel = false;
        const intervalCheck = async () => {
            setLoading(true);
            let preReg = 0;
            let one = 0;
            let two = 0;
            let three = 0;
            let next4 = 0;
            let four = 0;
            let over4 = 0;
            let over4plus = 0;
            let next12 = 0;
            let over12 = 0;
            let data;

            _.forEach(wardPatientsObject, patient => {
                    let elementHidden = false;
                    if (_.startsWith(patient?.bed, "wlist") || !patient?.bed) {
                        if (patient.admission?.locationDescription === "DISCHARGE") {
                            elementHidden = true;
                        }
                    }

                    const arrival = calculateArrivalDate(patient);
                    const duration = moment().diff(moment(arrival), "minutes");
                    if (patient?.admission?.status !== "DISCHARGE_REQUESTED" && patient?.wardId !== "0EDAU" && !elementHidden) {
                        if (duration <= 60) {
                            one++;
                        } else if (duration > 60 && duration <= 120) {
                            two++;
                        } else if (duration > 120 && duration <= 180) {
                            three++;
                        } else if (duration > 180 && duration <= 240) {
                            four++;
                        } else if (duration > 240 && duration <= 660) {
                            over4++;
                        } else if (duration > 660 && duration <= 720) {
                            next12++;
                        } else if (duration > 720) {
                            over12++;
                        }
                        if (duration > 240) {
                            over4plus++;
                        }
                    }
                }
            );

            await API.getPreReg(controller.signal).then((response) => {
                if (cancel) return;
                if (response?.status === 200) {
                    preReg = response?.data?.__data?.length;
                }
            });

            const startData = mode === "today"
                ? moment().utc().startOf('day').toISOString()
                : moment().subtract(1, "days").toISOString();

            let wardsId = [];
            if (config.modules?.map?.customMap) {
                const ed = `ED##${newCurrentMap?.hospitalId}`;
                wardsId.push(ed)
            } else {
                wardsId = ["ED"];
            }

            await API.getStatistic(startData, moment().toISOString(), wardsId, controller.signal)
                .then((response) => {
                    if (cancel) return;
                    if (response?.status === 200) {
                        data = response.data;
                    }
                });

            setLoading(false);

            return {
                preReg: preReg,
                counter: {
                    one,
                    two,
                    three,
                    four,
                    next4,
                    over4,
                    over4plus,
                    next12,
                    over12,
                },
                ...data
            };
        };

        intervalCheck().then(response => {
            setIntervalCheckState({
                ...response
            });
        });

        const interval = setInterval(() => {
            intervalCheck().then(response => {
                setIntervalCheckState({
                    ...response
                });
            });
        }, timeout ? timeout : 30000);

        return () => {
            cancel = true;
            clearInterval(interval);
        };
    }, [mode, newCurrentMap]);

    // Render ward bed stats component
    const renderWard = (wardStats) => {
        const renderInner = (icon, counter, text) => {
            return (
                <div className={classesLocal.wardBoxInfo}>
                    <Grid item xs={12}>
                        {icon}
                        <span className={classesLocal.wardBoxCounter}>{counter}</span>
                    </Grid>
                    <Grid item xs={12}>
                        {text}
                    </Grid>
                </div>
            );
        };

        return (
            <div className={classesLocal.wardBox}>
                <Grid item xs={12} className={classesLocal.wardBoxTitle}>
                    {_.toUpper(wardStats.wardName)}
                </Grid>
                <Grid container direction={"row"}>
                    <Grid item xs={6}>
                        {renderInner(<FunctionsIcon
                            className={clsx(classesLocal.wardBoxIcon, "total")}/>, wardStats.total, "Total")}
                    </Grid>
                    <Grid item xs={6}>
                        {renderInner(<LockIcon
                            className={clsx(classesLocal.wardBoxIcon, "lock")}/>, wardStats.locked, "Locked")}
                    </Grid>
                    <Grid item xs={6}>
                        {renderInner(<LocalHotelIcon
                            className={clsx(classesLocal.wardBoxIcon, "occupied")}/>, wardStats.occupied, "Occupied")}
                    </Grid>
                    <Grid item xs={6}>
                        {renderInner(<SingleBedIcon
                            className={clsx(classesLocal.wardBoxIcon, "free")}/>, wardStats.total - wardStats.locked - wardStats.occupied, "Free")}
                    </Grid>
                </Grid>
            </div>
        );
    };

    // Prepare data for ward bed stats
    const prepareStatsFroWard = (name, array) => {
        let locked = 0;
        let occupied = 0;

        _.forEach(array, bed => {
            if (!_.isEmpty(dashboardStats?.beds) && dashboardStats?.beds.includes(bed.bed)) {
                occupied++;
            }

            if (!_.isEmpty(statusesObject?.bedStatuses) && ["lock", "cleaning", "other", "ATMIST", "booking"].includes(statusesObject?.bedStatuses[String(bed.zoneId + bed.bed)]?.status)) {
                locked++;
            }
        });

        return {
            wardName: name,
            total: array.length,
            locked: locked,
            occupied: occupied
        };
    };

    const renderWaitingList = () => {
        const waitingRooms = _.filter(props.areas, {type: "waiting"});

        const checkWaiting = (wardId) => {
            let counter = 0;
            if (!_.isEmpty(dashboardStats?.beds)) {
                _.forEach(dashboardStats?.beds, bed => {
                    if (bed === wardId || (wardId === "wlist1" && !bed)) {
                        counter++;
                    }
                });
            }

            return counter;
        };

        return (
            _.sortBy(waitingRooms, ["areaTitle"]).map(wlist => {
                const bed = wlist.beds[0]?.parsed?.params[1];

                return <Grid item xs={12} md={3} key={bed} style={{marginTop: 15}}>
                    <div className={classesLocal.wardBox}>
                        <div className={classesLocal.wardBoxTitle}>
                            {t(handleListName(wlist.areaTitle))}
                        </div>
                        <div style={{textAlign: "center"}}>
                            <LocalHotelIcon className={clsx(classesLocal.wardBoxIcon, "occupied")}/>
                            <span className={classesLocal.wardBoxCounter}>
                                {checkWaiting(bed)}
                            </span>
                        </div>
                    </div>
                </Grid>;
            })
        );
    };

    const findPrettyAreaName = (bed) => {
        let nameArea = bed[0];
        if (!_.isEmpty(props.areas)) {
            let find;
            _.forEach(props.areas, area => {
                const bedName = _.lowerCase(bed[1]);

                if (!find) {
                    if (_.find(area.beds, el => {
                        return _.lowerCase(el.parsed.params[1]) === bedName;
                    })) {
                        find = area;
                    }
                }
            });

            if (find) {
                nameArea = find.areaTitle === "ZONE RH  (Resus/High Care)" ? "ZONE RH" : find.areaTitle;
            }
        }
        return nameArea;
    };

    // Render wards component
    const renderWards = () => {
        let ward = {};
        if (!_.isEmpty(props.beds)) {
            // joining beds with wards into object
            _.forEach(props.beds, bed => {
                const wardBed = bed?.obj?.parsed?.params[1];
                const wardName = findPrettyAreaName(bed?.obj?.parsed?.params);
                const zoneId = bed?.obj?.parsed?.params[0];

                if (ward[wardName]) {
                    ward[wardName] = [...ward[wardName],
                        {
                            name: bed?.obj?.parsed?.name,
                            bed: wardBed,
                            type: bed?.obj?.parsed?.type,
                            zoneId
                        }];
                } else {
                    ward[wardName] = [
                        {
                            name: bed?.obj?.parsed?.name,
                            bed: wardBed,
                            type: bed?.obj?.parsed?.type,
                            zoneId
                        }];
                }
            });
        }

        const listOfWard = _.sortBy(Object.keys(ward));

        if (!_.isEmpty(listOfWard)) {
            return (
                <Fragment>
                    <Grid item xs={12}>
                        <Grid container direction={"row"} spacing={2}>
                            {listOfWard.map(element => {
                                const wardStats = prepareStatsFroWard(element, ward[element]);

                                return (
                                    <Grid item xs={12} md={3} key={element}>
                                        {renderWard(wardStats)}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container direction={"row"} spacing={2}>
                            {renderWaitingList()}
                        </Grid>
                    </Grid>
                </Fragment>
            );
        }
    };

    // Render box component for first section
    const box = (icon, counter, text) => {
        return (
            <div className={classesLocal.box}>
                <Grid container justifyContent="space-between">
                    {icon}
                    <span className={classesLocal.boxCounter}>{counter ? counter : 0}</span>
                </Grid>
                <div className={classesLocal.boxText}>
                    {t(text)}
                </div>
            </div>
        );
    };

    // Render component with the longest time section
    const renderLongestTime = (type, timeType) => {
        // Format date
        const getTime = (time) => {
            if (time || time > 0) {
                const duration = timeType === "attendance" ? moment.duration(moment().diff(moment(time)))
                    : moment.duration(time, "minutes");

                if (duration < 0) {
                    return "-- : -- : -- ";
                }

                //Get Days
                const days = Math.floor(duration.asDays());
                const daysFormatted = days ? `${days}` : '0';

                //Get Hours
                const hours = duration.hours();
                const hoursFormatted = `${hours < 10 ? "0" + hours : hours}`;

                //Get Minutes
                const minutes = duration.minutes();
                const minutesFormatted = `${minutes < 10 ? "0" + minutes : minutes}`;

                return [daysFormatted, hoursFormatted, minutesFormatted].join(" : ");
            }
            return "-- : -- : -- ";
        };

        // render Text
        let text;
        switch (type) {
            case "seen": {
                text = "Longest wait to Triage";
                break;
            }
            case "longest": {
                text = "Longest Attendance in ED";
                break;
            }
            case "clerking": {
                text = "Longest Wait for Clerking Clinician";
                break;
            }
            case "clerkingAverage": {
                text = "Current Average wait for Clerking Clinician";
                break;
            }
            case "clerkingSeen": {
                text = "Current Average wait from Triage to Clerking Clinician";
                break;
            }
            case "ambulanceHandover": {
                text = "Current Average Ambulance Handover Time";
                break;
            }
            case "ambulanceAvarage": {
                text = "Average Ambulance Handover Time";
                break;
            }

            default: {
                text = "Current Average Wait to Triage";
                break;
            }
        }
        let pt;
        let time;

        if (timeType !== "average") {
            pt = dashboardStats?.[type] ? _.find(wardPatientsObject, {__id: dashboardStats?.[type]}) : null;
        }

        switch (timeType) {
            case "triage":
            case "clerking":
                const field = `${type}Time`
                time = dashboardStats?.[field];
                break;
            case "attendance":
                time = pt?.admission?.startDate;
                break;
            case "average":
                time = dashboardStats?.[type];
                break;
        }

        const css = type === "seen" || type === "clerking" ? "seen" : type === "longest" ? "longest" : "average";

        return (
            <Grid container direction={"row"} className={clsx(classesLocal.longestTimeBox, css)}>
                <Grid item xs={12} md={3}>
                    <HourglassTopIcon fontSize={"inherit"} className={classesLocal.longestTimeIcon}/>
                </Grid>
                <Grid item xs={12} md={9}>
                    <Grid item xs={12} className={classesLocal.longestTimeFormatLegend}>
                        {/*TODO change this*/}
                        {t(`days\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0hours\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0\xa0mins`)}
                    </Grid>
                    <Grid item xs={12} className={classesLocal.longestTimeFormat}>
                        {getTime(time)}
                    </Grid>
                </Grid>
                <Grid item xs={12} className={classesLocal.longestTimeMainText}>
                    {t(text)}
                </Grid>
                {timeType !== "average" &&
                    <Fragment>
                        <Grid item xs={12} className={classesLocal.longestTimeDescription}>
                            {t(pt ? `Admitted ${moment(pt?.admission?.startDate).format(config.defaultDateTimeFormat)}` : "-")}
                        </Grid>
                        <Grid item xs={12} className={classesLocal.longestTimeDescription}>
                            {t(pt ? `${pt?.patient?.__id}, ${pt?.patient?.familyName} ${pt?.patient?.firstName}` : "-")}
                        </Grid>
                    </Fragment>
                }
            </Grid>
        );
    };

    // Render Breaches components
    const renderBreach = () => {
        const renderBreachElement = (type, text) => {
            const counter = dashboardStats?.counter?.[type];
            const css = type === "over4" || type === "over12" ? "over" : type;

            return (
                <div className={clsx(classesLocal.breachBox, css)}>
                    <Grid item xs={12}>
                        <Grid container direction={"row"} alignItems="center" justifyContent="space-between">
                            <AccessTimeIcon className={classesLocal.icon}/>
                            <div className={classesLocal.breachCounter}>
                                {counter ? counter : 0}
                            </div>
                        </Grid>
                    </Grid>
                    <Grid item xs={12}>
                        <div className={classesLocal.breachText}>
                            {text}
                        </div>
                    </Grid>
                </div>
            );
        };

        return (
            <Grid container direction={"row"} spacing={2} columns={10}>
                <Grid item xs={12} md={4}>
                    {renderBreachElement("one", "Arrived 0-1hrs ago")}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderBreachElement("two", "Arrived 1-2hrs ago")}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderBreachElement("three", "Arrived 2-3hrs ago")}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderBreachElement("four", "Arrived 3-4hrs ago")}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderBreachElement("over4", "Current Breaches (4th hour)")}
                </Grid>
                <Grid item xs={12} md={4}>
                    {renderBreachElement("over12", "Current Breaches (12th hour)")}
                </Grid>
            </Grid>
        );
    };

    return (
        <Fragment>
            <DialogTitle className={clsx(classesLocal.background, classesLocal.dialogTitle)}>
                <Grid container direction={"row"}>
                    <Grid item xs={config?.modules?.reports?.extendedDashboard ? 11 : 12}>
                        {t("Dashboard")}
                    </Grid>
                </Grid>
            </DialogTitle>

            <DialogContent className={clsx(classesLocal.background, classesLocal.dialogContent, "scroll-normal-white")} dividers>
                {loading &&
                    <div className={classesLocal.loading}>
                        <CircularProgress
                            color={"info"}
                            size={100}
                        />
                    </div>
                }
                <Grid item xs={12} className={classesLocal.toggleButtons}>
                    <ToggleButtonGroup
                        className={classesLocal.toggleButtonsGroup}
                        value={mode}
                        exclusive
                        onChange={events => setMode(events.target.value)}
                    >
                        <ToggleButton value="today" className={classesLocal.toggle}>
                            {t("Today")}
                        </ToggleButton>

                        <ToggleButton value="24h" className={classesLocal.toggle}>
                            {t("Since 24h")}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>

                <Paper className={classesLocal.paper}>
                    <Grid container direction={"row"} spacing={2} style={{marginTop: 5}} columns={8}>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="injuryPt" className={classesLocal.icon}/>, dashboardStats?.currentPt,
                                "Current Number of Patients")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<PanToolIcon className={classesLocal.icon}/>, dashboardStats?.waitingWard,
                                "Current number of patients in Waiting Rooms")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<PeopleIcon className={classesLocal.icon}/>,
                                dashboardStats?.currentAttendance + dashboardStats?.dischargesFiltered,
                                "All Attendances")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<LogoutIcon className={classesLocal.icon}/>, dashboardStats?.discharges,
                                "All Discharges")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulanceArrivalAwaiting,
                                "Current Number of Ambulance Arrival Awaiting")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="injuryPt"
                                    className={classesLocal.icon}/>, dashboardStats?.clinicalConclusion,
                                "Current Number of Patients with Clinical Conclusion")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<PeopleIcon className={classesLocal.icon}/>, dashboardStats?.ptEDAU,
                                "Current Number of Patients Admitted to EDAU")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<EventNoteIcon className={classesLocal.icon}/>, dashboardStats?.preReg,
                                "Current Pre-Registration Patients")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="injuryPt" className={classesLocal.icon}/>, dashboardStats?.refOpen,
                                "Current Number of Patients with Open Specialty Referral")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="injuryPt" className={classesLocal.icon}/>, dashboardStats?.refAll,
                                "Current Number of Patients with Specialty Referral")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="bedRequested"  className={classesLocal.icon}/>, dashboardStats?.bedReq,
                                "Current Number of Patients with Bed Requested")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="bedReady"
                                                 className={classesLocal.icon}/>, dashboardStats?.bedReady,
                                "Current Number of Patients with Bed Ready")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AccessTimeIcon
                                    className={classesLocal.icon}/>, (dashboardStats?.counter?.over4plus + dashboardStats?.breaches4h),
                                "Breaches 4th+ Hour")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AccessTimeIcon
                                    className={classesLocal.icon}/>, (dashboardStats?.counter?.over12 + dashboardStats?.breaches12h),
                                "Breaches 12th+ Hour")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<LogoutIcon className={classesLocal.icon}/>, dashboardStats?.breaches4h,
                                "Discharges After 4 hour")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<LogoutIcon className={classesLocal.icon}/>, dashboardStats?.breaches12h,
                                "Discharges After 12 hour")}
                        </Grid>

                        {/* Ambulance Arrivals */}
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulanceArrivalBreaches,
                                "Current Number of Ambulance Arrivals Breaches")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulanceArrivalBreaches15,
                                "Current Number of Ambulance Arrival Breaches 15+ min")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulanceArrivalBreaches30,
                                "Current Number of Ambulance Arrival Breaches 30+ min")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulanceArrivalBreaches60,
                                "Current Number of Ambulance Arrival Breaches 60+ min")}
                        </Grid>

                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance" className={classesLocal.icon}/>, dashboardStats?.ambulanceAll,
                                "Total Number of Ambulance Arrivals")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulance15min,
                                "Total Number of Ambulance Arrival Breaches 15+ min")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulanceOver30min,
                                "Total Number of Ambulance Arrival Breaches 30+ min")}
                        </Grid>
                        <Grid item xs={8} md={2}>
                            {box(<AgyleNamedIcon name="ambulance"
                                    className={classesLocal.icon}/>, dashboardStats?.ambulanceOver60min,
                                "Total Number of Ambulance Arrival Breaches 60+ min")}
                        </Grid>


                    </Grid>
                </Paper>

                <Grid item xs={12}>
                    <Paper className={classesLocal.paper}>
                        <h2>{t("Current Bed Statistics in Area")}</h2>
                        {renderWards()}
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper className={classesLocal.paper}>
                        <Grid container direction={"row"} spacing={2} style={{marginTop: 5}}>
                            <Grid item xs={12} md={4}>
                                {renderLongestTime("seen", "triage")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {renderLongestTime("clerking", "clerking")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {renderLongestTime("longest", "attendance")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {renderLongestTime("average", "average")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {renderLongestTime("clerkingAverage", "average")}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                {renderLongestTime("clerkingSeen", "average")}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderLongestTime("ambulanceHandover", "average")}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {renderLongestTime("ambulanceAvarage", "average")}
                            </Grid>

                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper className={classesLocal.paper}>
                        <h2>{t("ED Breach Profile")}</h2>
                        <Grid container direction={"row"} spacing={2}>
                            <Grid item xs={12} md={6}>
                                {box(<AccessTimeIcon
                                        className={classesLocal.icon}/>, dashboardStats?.counter?.four,
                                    "Breaches in Next Hour (4th+ hour)")}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {box(<AccessTimeIcon
                                        className={classesLocal.icon}/>, dashboardStats?.counter?.next12,
                                    "Breaches in Next Hour (12th+ hour)")}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                {renderBreach()}
                            </Grid>
                            <Grid item xs={12} md={6} style={{marginTop: 10}}>
                                {dashboardStats?.dataChart &&
                                    <Doughnut
                                        data={dashboardStats?.dataChart}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: {
                                                    labels: {
                                                        color: theme.palette.mode === 'light' ? "black" : "white",
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                }
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

            </DialogContent>

            <DialogActions>
                <div style={{flex: '1 0 0'}}>
                    <Button color="tertiary" onClick={props.onClose}>{t("Close")}</Button>
                </div>
            </DialogActions>
        </Fragment>
    );
};

export default Dashboard;

export const handleListName = (name) => {
    const newName = WLIST_NAME_MAPPER[name] || name;
    return _.upperCase(newName);
}